import React from "react";
import { Box, Typography } from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

/**
 * "There is nothing real to show here yet", over a blurred copy of what this
 * panel will eventually hold.
 *
 * Blurred rather than dropped: the card is part of the plant, and an empty slot
 * in the grid reads as a bug. What sits underneath is a template — legible as a
 * shape, deliberately not as values — so nobody can read a number off it. The
 * message layer takes the pointer, so there is nothing to hover or click
 * through to.
 *
 * The caller is responsible for not putting live state underneath: this hides
 * values, it does not make them safe.
 */
const UnavailablePanel: React.FC<{
  /** Why there is nothing to show. Shown over the blur. */
  reason: string;
  children: React.ReactNode;
}> = ({ reason, children }) => (
  <Box sx={{ position: "relative" }}>
    <Box
      aria-hidden
      sx={{
        filter: "blur(7px)",
        opacity: 0.5,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {children}
    </Box>
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        px: 2,
        textAlign: "center",
      }}
    >
      <VisibilityOffOutlinedIcon sx={{ fontSize: 34, color: "text.disabled" }} />
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {reason}
      </Typography>
    </Box>
  </Box>
);

export default UnavailablePanel;
